<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <div class="app-container">
    <!-- Header Section with Logo, Dashboard, Profile, and Logout -->
    <Header />

    <!-- Sidebar and Main Content -->
    <main :class="mainContentClass">
      <div class="main-header">
        <h1 class="main-title">Dashboard</h1>
        <div class="header-actions">
          <button class="main-btn" @click="navigateToNewFlight">
            <vue-feather type="plus" size="18" />
            <span>New Listing</span>
          </button>
        </div>
      </div>

      <Divider />
      <br />

      <!-- Dashboard Stats Section -->
      <div class="dashboard-stats">
        <div class="section-header">
          <h2 class="header-title">Booking Statistics</h2>
          <p class="section-description">
            Overview of your booking activity and flight statistics.
          </p>
        </div>

        <div class="stats-cards">
          <div class="stat-card">
            <h2 class="stat-number">{{ totalBookings }}</h2>
            <p class="stat-label">Total Bookings</p>
            <vue-feather
              class="stat-icon"
              type="calendar"
              size="20"
            ></vue-feather>
          </div>

          <div class="stat-card">
            <h2 class="stat-number">{{ completedBookings }}</h2>
            <p class="stat-label">Completed Flights</p>
            <vue-feather
              class="stat-icon"
              type="check-circle"
              size="20"
            ></vue-feather>
          </div>

          <div class="stat-card">
            <h2 class="stat-number">{{ upcomingBookings }}</h2>
            <p class="stat-label">Upcoming Flights</p>
            <vue-feather class="stat-icon" type="clock" size="20"></vue-feather>
          </div>

          <div class="stat-card">
            <h2 class="stat-number">{{ cancelledBookings }}</h2>
            <p class="stat-label">Cancelled Flights</p>
            <vue-feather
              class="stat-icon"
              type="x-circle"
              size="20"
            ></vue-feather>
          </div>
        </div>
      </div>

      <br />
      <Divider />
      <br />

      <h2 class="header-title">Your Reservations</h2>

      <!-- Table -->
      <div class="table-container">
        <div v-if="isLoading" class="loading-state">
          <vue-feather type="loader" size="24" class="spin" />
          <span class="loading-text">Loading your reservations...</span>
        </div>
        <div v-else-if="allReservations.length === 0" class="empty-state">
          <vue-feather type="calendar" size="48" style="color: #aaa" />
          <br />
          <p class="empty-message">You don't have any reservations yet.</p>
        </div>
        <div v-else>
          <!-- Upcoming Reservations -->
          <div class="section-header">
            <h3>Upcoming Reservations</h3>
          </div>
          <div v-if="upcomingReservations.length === 0" class="empty-section">
            <p>No upcoming reservations</p>
          </div>

          <!-- Desktop Table Layout -->
          <div v-else class="reservation-table desktop-table">
            <div class="table-header">
              <div class="th time-column">EVENT TIME</div>
              <div class="th listing-column">LISTING</div>
              <div class="th guest-column">GUEST</div>
              <div class="th status-column">STATUS</div>
              <div class="th actions-column">ACTIONS</div>
            </div>
            <div class="table-body">
              <div
                v-for="reservation in upcomingReservations"
                :key="reservation.id"
                class="table-row"
              >
                <div class="td time-column">
                  <div class="time-info">
                    <div class="date-display">
                      <div class="calendar-date">
                        <div class="calendar-month">
                          {{
                            formatEventDate(
                              reservation.eventSchedule?.listing?.date,
                              "month"
                            )
                          }}
                        </div>
                        <div class="calendar-day">
                          {{
                            formatEventDate(
                              reservation.eventSchedule?.listing?.date,
                              "day"
                            )
                          }}
                        </div>
                      </div>
                    </div>
                    <div class="time-wrapper">
                      <span class="time">{{
                        reservation.eventSchedule?.listing?.time
                      }}</span>
                      <span class="timezone">{{
                        reservation.eventSchedule?.listing?.timezone
                      }}</span>
                    </div>
                  </div>
                </div>
                <div class="td listing-column">
                  <div class="listing-info">
                    <img
                      :src="
                        reservation.listingDetails?.bannerUrl ||
                        require(`@/assets/placeholder-image-listing.png`)
                      "
                      class="listing-image"
                      alt="Listing"
                    />
                    <div class="listing-details">
                      <span class="listing-name">{{
                        reservation.listingDetails?.title || "Loading..."
                      }}</span>
                      <span class="listing-location">{{
                        reservation.listingDetails?.location ||
                        "Location unavailable"
                      }}</span>
                    </div>
                  </div>
                </div>
                <div class="td guest-column">
                  <div class="guest-info">
                    <div class="guest-avatar">
                      <vue-feather type="user" size="18" class="avatar-icon" />
                    </div>
                    <div class="guest-details">
                      <span class="guest-name">{{
                        reservation.guestCount > 1
                          ? "Skyhost"
                          : reservation.guestDetails?.name || "Guest"
                      }}</span>
                      <span class="guest-count"
                        >{{ reservation.guestCount }}
                        {{
                          reservation.guestCount === 1 ? "Guest" : "Guests"
                        }}</span
                      >
                    </div>
                  </div>
                </div>
                <div class="td status-column">
                  <span :class="['status-badge', getStatusClass(reservation)]">
                    {{ getStatusText(reservation) }}
                  </span>
                </div>
                <div class="td actions-column">
                  <button
                    v-if="canStartReservation(reservation)"
                    class="action-btn start-btn"
                    :disabled="!reservation.isConfirmed"
                    @click="startBoarding(reservation)"
                  >
                    Start
                  </button>
                  <button
                    v-if="canCancelReservation(reservation)"
                    class="action-btn cancel-btn"
                    @click="showCancelModal(reservation)"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Card Layout -->
          <div class="reservation-cards mobile-cards">
            <div
              v-for="reservation in upcomingReservations"
              :key="reservation.id"
              class="reservation-card-mobile"
            >
              <div class="card-header-mobile">
                <div class="time-info-mobile">
                  <div class="calendar-date-mobile">
                    <div class="calendar-month-mobile">
                      {{
                        formatEventDate(
                          reservation.eventSchedule?.listing?.date,
                          "month"
                        )
                      }}
                    </div>
                    <div class="calendar-day-mobile">
                      {{
                        formatEventDate(
                          reservation.eventSchedule?.listing?.date,
                          "day"
                        )
                      }}
                    </div>
                  </div>
                  <div class="time-details-mobile">
                    <span class="time-mobile">{{
                      reservation.eventSchedule?.listing?.time
                    }}</span>
                    <span class="timezone-mobile">{{
                      reservation.eventSchedule?.listing?.timezone
                    }}</span>
                  </div>
                </div>
                <span
                  :class="['status-badge-mobile', getStatusClass(reservation)]"
                >
                  {{ getStatusText(reservation) }}
                </span>
              </div>

              <div class="card-content-mobile">
                <div class="listing-info-mobile">
                  <img
                    :src="
                      reservation.listingDetails?.bannerUrl ||
                      require(`@/assets/placeholder-image-listing.png`)
                    "
                    class="listing-image-mobile"
                    alt="Listing"
                  />
                  <div class="listing-details-mobile">
                    <span class="listing-name-mobile">{{
                      reservation.listingDetails?.title || "Loading..."
                    }}</span>
                    <span class="listing-location-mobile">{{
                      reservation.listingDetails?.location ||
                      "Location unavailable"
                    }}</span>
                  </div>
                </div>

                <div class="guest-info-mobile">
                  <div class="guest-avatar-mobile">
                    <vue-feather
                      type="user"
                      size="16"
                      class="avatar-icon-mobile"
                    />
                  </div>
                  <div class="guest-details-mobile">
                    <span class="guest-name-mobile">{{
                      reservation.guestCount > 1
                        ? "Skyhost"
                        : reservation.guestDetails?.name || "Guest"
                    }}</span>
                    <span class="guest-count-mobile"
                      >{{ reservation.guestCount }}
                      {{
                        reservation.guestCount === 1 ? "Guest" : "Guests"
                      }}</span
                    >
                  </div>
                </div>

                <div
                  class="actions-mobile"
                  v-if="
                    canStartReservation(reservation) ||
                    canCancelReservation(reservation)
                  "
                >
                  <button
                    v-if="canStartReservation(reservation)"
                    class="action-btn-mobile start-btn-mobile"
                    :disabled="!reservation.isConfirmed"
                    @click="startBoarding(reservation)"
                  >
                    Start
                  </button>
                  <button
                    v-if="canCancelReservation(reservation)"
                    class="action-btn-mobile cancel-btn-mobile"
                    @click="showCancelModal(reservation)"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Past Reservations -->
          <div class="section-header past-section">
            <h3>Past Reservations</h3>
          </div>
          <div v-if="pastReservations.length === 0" class="empty-section">
            <p>No past reservations</p>
          </div>
          <div v-else class="reservation-table desktop-table">
            <div class="table-header">
              <div class="th time-column">EVENT TIME</div>
              <div class="th listing-column">LISTING</div>
              <div class="th guest-column">GUEST</div>
              <div class="th status-column">STATUS</div>
              <div class="th actions-column">ACTIONS</div>
            </div>
            <div class="table-body">
              <div
                v-for="reservation in pastReservations"
                :key="reservation.id"
                class="table-row"
              >
                <div class="td time-column">
                  <div class="time-info">
                    <div class="date-display">
                      <div class="calendar-date">
                        <div class="calendar-month">
                          {{
                            formatEventDate(
                              reservation.eventSchedule?.listing?.date,
                              "month"
                            )
                          }}
                        </div>
                        <div class="calendar-day">
                          {{
                            formatEventDate(
                              reservation.eventSchedule?.listing?.date,
                              "day"
                            )
                          }}
                        </div>
                      </div>
                    </div>
                    <div class="time-wrapper">
                      <span class="time">{{
                        reservation.eventSchedule?.listing?.time
                      }}</span>
                      <span class="timezone">{{
                        reservation.eventSchedule?.listing?.timezone
                      }}</span>
                    </div>
                  </div>
                </div>
                <div class="td listing-column">
                  <div class="listing-info">
                    <img
                      :src="
                        reservation.listingDetails?.bannerUrl ||
                        require(`@/assets/placeholder-image-listing.png`)
                      "
                      class="listing-image"
                      alt="Listing"
                    />
                    <div class="listing-details">
                      <span class="listing-name">{{
                        reservation.listingDetails?.title || "Loading..."
                      }}</span>
                      <span class="listing-location">{{
                        reservation.listingDetails?.location ||
                        "Location unavailable"
                      }}</span>
                    </div>
                  </div>
                </div>
                <div class="td guest-column">
                  <div class="guest-info">
                    <div class="guest-avatar">
                      <vue-feather type="user" size="18" class="avatar-icon" />
                    </div>
                    <div class="guest-details">
                      <span class="guest-name">{{
                        reservation.guestCount > 1
                          ? "Skyhost"
                          : reservation.guestDetails?.name || "Guest"
                      }}</span>
                      <span class="guest-count"
                        >{{ reservation.guestCount }}
                        {{
                          reservation.guestCount === 1 ? "Guest" : "Guests"
                        }}</span
                      >
                    </div>
                  </div>
                </div>
                <div class="td status-column">
                  <span :class="['status-badge', getStatusClass(reservation)]">
                    {{ getStatusText(reservation) }}
                  </span>
                </div>
                <div class="td actions-column">
                  <button
                    v-if="canStartReservation(reservation)"
                    class="action-btn start-btn"
                    :disabled="!reservation.isConfirmed"
                    @click="startBoarding(reservation)"
                  >
                    <vue-feather type="play" size="16" />
                    Start
                  </button>
                  <button
                    v-if="canCancelReservation(reservation)"
                    class="action-btn cancel-btn"
                    @click="showCancelModal(reservation)"
                  >
                    <vue-feather type="x" size="16" />
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Mobile Card Layout for Past Reservations -->
          <div class="reservation-cards mobile-cards">
            <div
              v-for="reservation in pastReservations"
              :key="reservation.id"
              class="reservation-card-mobile"
            >
              <div class="card-header-mobile">
                <div class="time-info-mobile">
                  <div class="calendar-date-mobile">
                    <div class="calendar-month-mobile">
                      {{
                        formatEventDate(
                          reservation.eventSchedule?.listing?.date,
                          "month"
                        )
                      }}
                    </div>
                    <div class="calendar-day-mobile">
                      {{
                        formatEventDate(
                          reservation.eventSchedule?.listing?.date,
                          "day"
                        )
                      }}
                    </div>
                  </div>
                  <div class="time-details-mobile">
                    <span class="time-mobile">{{
                      reservation.eventSchedule?.listing?.time
                    }}</span>
                    <span class="timezone-mobile">{{
                      reservation.eventSchedule?.listing?.timezone
                    }}</span>
                  </div>
                </div>
                <span
                  :class="['status-badge-mobile', getStatusClass(reservation)]"
                >
                  {{ getStatusText(reservation) }}
                </span>
              </div>

              <div class="card-content-mobile">
                <div class="listing-info-mobile">
                  <img
                    :src="
                      reservation.listingDetails?.bannerUrl ||
                      require(`@/assets/placeholder-image-listing.png`)
                    "
                    class="listing-image-mobile"
                    alt="Listing"
                  />
                  <div class="listing-details-mobile">
                    <span class="listing-name-mobile">{{
                      reservation.listingDetails?.title || "Loading..."
                    }}</span>
                    <span class="listing-location-mobile">{{
                      reservation.listingDetails?.location ||
                      "Location unavailable"
                    }}</span>
                  </div>
                </div>

                <div class="guest-info-mobile">
                  <div class="guest-avatar-mobile">
                    <vue-feather
                      type="user"
                      size="16"
                      class="avatar-icon-mobile"
                    />
                  </div>
                  <div class="guest-details-mobile">
                    <span class="guest-name-mobile">{{
                      reservation.guestCount > 1
                        ? "Skyhost"
                        : reservation.guestDetails?.name || "Guest"
                    }}</span>
                    <span class="guest-count-mobile"
                      >{{ reservation.guestCount }}
                      {{
                        reservation.guestCount === 1 ? "Guest" : "Guests"
                      }}</span
                    >
                  </div>
                </div>

                <div
                  class="actions-mobile"
                  v-if="
                    canStartReservation(reservation) ||
                    canCancelReservation(reservation)
                  "
                >
                  <button
                    v-if="canStartReservation(reservation)"
                    class="action-btn-mobile start-btn-mobile"
                    :disabled="!reservation.isConfirmed"
                    @click="startBoarding(reservation)"
                  >
                    Start
                  </button>
                  <button
                    v-if="canCancelReservation(reservation)"
                    class="action-btn-mobile cancel-btn-mobile"
                    @click="showCancelModal(reservation)"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </main>
    <!-- Footer -->
    <!-- <footer class="footer">
      <p>&copy; 2024 Skyhost. All rights reserved.</p>
    </footer> -->

    <!-- Add this at the end of the template, before closing </div> -->
    <CancelReservationModal
      v-if="selectedReservation && showCancelReservationModal"
      :reservation="selectedReservation"
      :listing="selectedListing"
      @close="closeCancelModal"
      @cancelled="handleReservationCancelled"
    />
  </div>
</template>

<script>
import { auth } from "@/plugins/firebase";
import { signOut } from "firebase/auth";
import { useSessionStore } from "@/stores/session";
import {
  getFirestore,
  collection,
  doc,
  getDoc,
  query,
  where,
  onSnapshot,
  orderBy,
  getDocs,
  limit,
} from "firebase/firestore";
import { isMobile } from "@/utils/helper";
import { computed } from "vue";
import Header from "@/components/organisms/Header.vue";
import CancelReservationModal from "@/components/organisms/CancelReservationModal.vue";
import { DateTime } from "luxon";
import Divider from "@/components/organisms/Divider.vue";

export default {
  data() {
    return {
      reservations: [],
      isLoading: true,
      listingDetails: {},
      guestDetails: {},
      showCancelReservationModal: false,
      selectedReservation: null,
      selectedListing: null,
      totalBookings: 0,
      completedBookings: 0,
      upcomingBookings: 0,
      cancelledBookings: 0,
    };
  },
  components: {
    Header,
    CancelReservationModal,
    Divider,
  },
  methods: {
    navigateToNewFlight() {
      this.$router.push("/list-an-event");
    },
    startBoarding(reservation) {
      this.$router.push({
        path: "/new-flight",
        query: {
          slotId: reservation.slotId,
          listingId: reservation.listingId,
          consolidatedReservationIds: reservation.consolidatedReservations
            .map((r) => r.id)
            .join(","),
        },
      });
    },
    async fetchReservations() {
      try {
        this.isLoading = true;
        const userId = auth.currentUser.uid;
        const db = getFirestore();

        const reservationsQuery = query(
          collection(db, "reservations"),
          where("listingUserId", "==", userId),
          orderBy("selectedDate", "desc")
        );

        this.unsubscribe = onSnapshot(
          reservationsQuery,
          async (snapshot) => {
            try {
              const reservationsData = [];

              for (const docSnapshot of snapshot.docs) {
                try {
                  const reservation = {
                    id: docSnapshot.id,
                    ...docSnapshot.data(),
                  };

                  // Fetch listing details
                  if (reservation.listingId) {
                    const listingRef = doc(
                      db,
                      "listings",
                      reservation.listingId
                    );
                    const listingDoc = await getDoc(listingRef);
                    if (listingDoc.exists()) {
                      reservation.listingDetails = listingDoc.data();
                    } else {
                      console.warn(
                        `Listing ${reservation.listingId} not found`
                      );
                    }
                  } else {
                    console.warn(
                      "No listingId found in reservation:",
                      reservation.id
                    );
                  }

                  // Fetch guest details
                  if (reservation.createdBy) {
                    // Query users collection where userId equals the createdBy value
                    const q = query(
                      collection(db, "users"),
                      where("userId", "==", reservation.createdBy),
                      limit(1)
                    );

                    const userSnapshot = await getDocs(q);
                    if (!userSnapshot.empty) {
                      reservation.guestDetails = userSnapshot.docs[0].data();
                    } else {
                      console.warn(
                        `User with userId ${reservation.createdBy} not found`
                      );
                    }
                  } else {
                    console.warn(
                      "No userId found in reservation:",
                      reservation.id
                    );
                  }

                  reservationsData.push(reservation);
                } catch (docError) {
                  console.error("Error processing reservation doc:", docError);
                }
              }

              this.reservations =
                this.consolidateReservations(reservationsData);
            } catch (snapshotError) {
              console.error("Error processing snapshot:", snapshotError);
            } finally {
              this.isLoading = false;
            }
          },
          (error) => {
            console.error("Snapshot listener error:", error);
            this.isLoading = false;
          }
        );
      } catch (error) {
        console.error("Error setting up reservation listener:", error);
        this.isLoading = false;
      }
    },
    consolidateReservations(reservationsData) {
      // Group reservations by slotId and cancellation status
      const slotGroups = new Map();

      reservationsData.forEach((reservation) => {
        // Create a unique key combining slotId and cancellation status
        const slotKey = `${reservation.slotId}_${
          reservation.isCancelled ? "cancelled" : "active"
        }`;

        if (!slotGroups.has(slotKey)) {
          // First reservation for this slot+status - use it as the base
          slotGroups.set(slotKey, {
            ...reservation,
            guestCount: reservation.guestCount || 1,
            consolidatedReservations: [reservation],
            consolidatedGuestDetails: [reservation.guestDetails],
          });
        } else {
          // Additional reservation for the same slot+status - add guest count
          const existingSlot = slotGroups.get(slotKey);
          existingSlot.guestCount += reservation.guestCount || 1;
          existingSlot.consolidatedReservations.push(reservation);
          existingSlot.consolidatedGuestDetails.push(reservation.guestDetails);
        }
      });

      // Convert map back to array and filter out any empty slots
      return Array.from(slotGroups.values()).filter(
        (slot) =>
          slot.guestCount > 0 && slot.consolidatedReservations.length > 0
      );
    },
    logout() {
      signOut(auth)
        .then(() => {
          const sessionStore = useSessionStore();
          sessionStore.clearUser();
          this.$router.push("/");
        })
        .catch(() => {
          this.$router.push("/");
        });
    },
    formatEventDate(dateString, part) {
      if (!dateString) return "";

      // Parse date like "March 13, 2025"
      const parts = dateString.split(/[\s,]+/); // Split by spaces and commas

      if (part === "month" && parts.length > 0) {
        // Return the month in uppercase abbreviation (e.g., "MAR")
        return parts[0].substring(0, 3).toUpperCase();
      } else if (part === "day" && parts.length > 1) {
        // Return the day as a number
        return parseInt(parts[1]);
      }
      return "";
    },
    formatMonth(date) {
      if (!date) return "";
      return new Date(date)
        .toLocaleDateString("en-US", { month: "short" })
        .toUpperCase();
    },
    formatDay(date) {
      if (!date) return "";
      return new Date(date).getDate();
    },
    formatWeekday(date) {
      if (!date) return "";
      return new Date(date).toLocaleDateString("en-US", { weekday: "short" });
    },
    formatTime(timeString) {
      if (!timeString) return "";
      return timeString;
    },
    formatGuestTime(timeString, guestTimezone) {
      if (!timeString) return "";
      try {
        // Combine date and time for proper timezone conversion
        const dateTime = new Date(`2024-01-01T${timeString}`); // Using fixed date since we only care about time

        // Convert to guest timezone if available
        if (guestTimezone) {
          return dateTime.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
            timeZone: guestTimezone,
            hour12: true,
          });
        }
        return timeString;
      } catch (error) {
        console.error("Error formatting guest time:", error);
        return timeString;
      }
    },
    getStatusClass(reservation) {
      // Handle special status cases first
      if (reservation.status === "cancelled") return "cancelled";
      if (reservation.status === "completed") return "completed";
      if (!reservation.isConfirmed) return "pending";

      // Get reservation date and time data
      const reservationDateStr = reservation.eventSchedule?.listing?.date;
      const reservationTimeStr = reservation.eventSchedule?.listing?.time;
      const reservationTimezone = reservation.eventSchedule?.listing?.timezone;

      // If we don't have essential data, default to pending
      if (!reservationDateStr || !reservationTimeStr || !reservationTimezone)
        return "pending";

      try {
        // Parse date string (e.g., "March 10, 2025")
        const dateParts = reservationDateStr.split(/[\s,]+/);
        const month = dateParts[0]; // "March"
        const day = parseInt(dateParts[1]); // 10
        const year = parseInt(dateParts[2]); // 2025

        // Parse time string (e.g., "10:30 PM")
        const timeMatch = reservationTimeStr.match(/(\d+):(\d+)\s*(AM|PM)/i);
        if (!timeMatch) return "pending"; // Invalid time format

        let hours = parseInt(timeMatch[1]);
        const minutes = parseInt(timeMatch[2]);
        const meridiem = timeMatch[3].toUpperCase();

        // Convert to 24-hour format
        if (meridiem === "PM" && hours < 12) hours += 12;
        else if (meridiem === "AM" && hours === 12) hours = 0;

        // Convert month name to month number (0-11)
        const monthMap = {
          January: 1,
          February: 2,
          March: 3,
          April: 4,
          May: 5,
          June: 6,
          July: 7,
          August: 8,
          September: 9,
          October: 10,
          November: 11,
          December: 12,
        };
        const monthNumber = monthMap[month];

        // Create a Luxon DateTime in the listing's timezone
        const reservationDateTime = DateTime.fromObject(
          {
            year,
            month: monthNumber,
            day,
            hour: hours,
            minute: minutes,
          },
          { zone: reservationTimezone }
        );

        // Get current time in the user's browser timezone
        const now = DateTime.now();

        // For easier debugging
        const oneHourAfterReservation = reservationDateTime.plus({ hours: 1 });

        // Compare the times
        if (now < reservationDateTime) {
          return "upcoming";
        } else {
          // Check if current time is within 1 hour after reservation time
          if (now <= oneHourAfterReservation) {
            return "active";
          } else {
            return "past";
          }
        }
      } catch (error) {
        console.error(
          `Error determining status for ${reservation.listingDetails?.title}:`,
          error
        );
        return "pending"; // Default to pending on error
      }
    },
    getStatusText(reservation) {
      const statusMap = {
        cancelled: "Cancelled",
        completed: "Completed",
        pending: "Pending",
        past: "Past",
        upcoming: "Upcoming",
        active: "In Progress",
      };
      return statusMap[this.getStatusClass(reservation)];
    },
    canStartReservation(reservation) {
      const status = this.getStatusClass(reservation);
      return (
        reservation.isConfirmed &&
        (status === "upcoming" || status === "active")
      );
    },
    canCancelReservation(reservation) {
      const status = this.getStatusClass(reservation);
      return (
        status !== "cancelled" && status !== "completed" && status !== "past"
      );
    },
    async showCancelModal(reservation) {
      try {
        // Fetch the complete listing document
        const db = getFirestore();
        const listingRef = doc(db, "listings", reservation.listingId);
        const listingDoc = await getDoc(listingRef);

        if (!listingDoc.exists()) {
          this.toast.error("Could not find listing details");
          return;
        }

        this.selectedListing = {
          id: listingDoc.id,
          ...listingDoc.data(),
        };
        this.selectedReservation = reservation;
        this.showCancelReservationModal = true;
      } catch (error) {
        console.error("Error fetching listing details:", error);
        this.toast.error("Could not load cancellation dialog");
      }
    },
    closeCancelModal() {
      this.showCancelReservationModal = false;
      this.selectedReservation = null;
      this.selectedListing = null;
    },
    handleReservationCancelled() {
      // The reservation will be automatically updated in the UI
      // because we're using onSnapshot listener in fetchReservations
    },
    cancelEvent(reservationId) {
      const reservation = this.reservations.find((r) => r.id === reservationId);
      if (reservation) {
        this.showCancelModal(reservation);
      }
    },
    updateBookingStats() {
      // Calculate stats based on reservations
      this.totalBookings = this.reservations.length;
      this.completedBookings = this.reservations.filter(
        (r) =>
          new Date(
            r.eventSchedule.user.date + " " + r.eventSchedule.user.time
          ) < new Date() && !r.isCancelled
      ).length;
      this.upcomingBookings = this.reservations.filter(
        (r) =>
          new Date(
            r.eventSchedule.user.date + " " + r.eventSchedule.user.time
          ) >= new Date() && !r.isCancelled
      ).length;
      this.cancelledBookings = this.reservations.filter(
        (r) => r.isCancelled
      ).length;
    },
  },
  setup() {
    const mainContentClass = computed(() => {
      return isMobile() ? "mobile-main-content" : "web-main-content";
    });

    return {
      mainContentClass,
    };
  },
  mounted() {
    this.fetchReservations();
  },
  beforeUnmount() {
    if (this.unsubscribe) {
      this.unsubscribe();
    }
  },
  computed: {
    allReservations() {
      return this.reservations;
    },
    upcomingReservations() {
      return this.reservations
        .filter((reservation) => {
          const status = this.getStatusClass(reservation);
          return status === "upcoming" || status === "active";
        })
        .sort((a, b) => {
          // Use eventSchedule.listing.date for sorting
          const dateA = new Date(a.eventSchedule?.listing?.date || "");
          const dateB = new Date(b.eventSchedule?.listing?.date || "");
          return dateA - dateB;
        });
    },
    pastReservations() {
      return this.reservations
        .filter((reservation) => {
          const status = this.getStatusClass(reservation);
          return status === "past" || status === "cancelled";
        })
        .sort((a, b) => {
          // Use eventSchedule.listing.date for sorting
          const dateA = new Date(a.eventSchedule?.listing?.date || "");
          const dateB = new Date(b.eventSchedule?.listing?.date || "");
          return dateB - dateA;
        });
    },
  },
  watch: {
    reservations: {
      handler() {
        this.updateBookingStats();
      },
      deep: true,
      immediate: true,
    },
  },
};
</script>

<style scoped>
.app-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  background-color: #fff;
}

/* Main Content */
.mobile-main-content {
  margin-top: 20px;
  padding: 20px;
}

.web-main-content {
  padding: 8rem 10rem;
}

.main-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.main-title {
  font-size: 3rem;
  color: #000;
}

.header-actions {
  display: flex;
  gap: 1rem;
}

.main-btn {
  background-color: #000;
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  height: 48px;
}

.main-btn:hover {
  opacity: 0.9;
}

.main-btn span {
  display: inline-block;
  line-height: 1;
}

.logout-button {
  background-color: #e5e7eb !important;
  border: none;
  border-radius: 12px;
  color: white;
  width: 45px;
  height: 45px;
  display: flex;
  align-items: center;
  justify-content: center;
  outline: none;
}

.logout-button:hover {
  background-color: rgb(235, 235, 235) !important;
}

/* Table Styles */
.table-container {
  margin-top: 2rem;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  padding: 20px;
  background-color: #f9fafb;
}

.empty-message {
  margin-top: 1rem;
  color: #6b7280;
  font-size: 0.875rem;
}

.reservation-table {
  width: 100%;
  border-radius: 12px;
  overflow: hidden;
}

.table-header {
  display: flex;
  background-color: #f9fafb;
  border-bottom: 1px solid #eee;
  padding: 1rem 1.5rem;
}

.table-row {
  display: flex;
  border-bottom: 1px solid #eee;
  align-items: center;
  padding: 0.75rem 1.5rem;
}

.table-row:hover {
  background-color: #f9fafb;
}

.th,
.td {
  font-size: 0.875rem;
  padding: 0.5rem;
}

.th {
  font-weight: 600;
  color: #4b5563;
  font-size: 0.813rem;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

/* Column width distribution */
.time-column {
  width: 15%;
  flex: 0 0 15%;
}

.listing-column {
  width: 28%;
  flex: 0 0 28%;
}

.guest-column {
  width: 15%;
  flex: 0 0 15%;
}

.status-column {
  width: 10%;
  flex: 0 0 10%;
}

.actions-column {
  width: 15%;
  flex: 0 0 15%;
  display: flex;
  gap: 10px;
}

/* Column specific styles */
.listing-info {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.listing-image {
  width: 64px;
  height: 48px;
  border-radius: 8px;
  object-fit: cover;
  border: 1px solid #e5e7eb;
}

.listing-details {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.listing-name {
  font-weight: 400;
  color: #111827;
  margin-bottom: 0.25rem;
  line-height: 1.25;
  font-size: 1rem;
  color: #222;
}

.listing-location {
  font-size: 0.75rem;
  color: #6b7280;
  line-height: 1.25;
}

.date-info {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.date {
  font-size: 1rem;
  font-weight: 600;
  color: #111827;
  letter-spacing: -0.025em;
}

.time {
  font-size: 0.875rem;
  color: #6b7280;
}

.guest-info {
  display: flex;
  align-items: center;
  gap: 0.875rem;
}

.guest-avatar {
  width: 40px;
  height: 40px;
  background-color: #f3f4f6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
}

.guest-avatar svg {
  width: 18px;
  height: 18px;
  stroke-width: 1.5px;
}

.guest-details {
  display: flex;
  flex-direction: column;
}

.guest-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
  margin-bottom: 0.125rem;
}

.guest-count {
  font-size: 0.75rem;
  color: #6b7280;
}

.status-badge {
  padding: 0.375rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  white-space: nowrap;
}

.status-badge.upcoming {
  background-color: #ecfdf5;
  color: #059669;
}

.status-badge.active {
  background-color: #dbeafe;
  color: #2563eb;
}

.status-badge.completed {
  background-color: #eff6ff;
  color: #3b82f6;
}

.status-badge.cancelled {
  background-color: #fee2e2;
  color: #dc2626;
}

.status-badge.past {
  background-color: #f3f4f6;
  color: #6b7280;
}

.status-badge.pending {
  background-color: #fef3c7;
  color: #d97706;
}

.action-btn {
  padding: 0.5rem 0.875rem;
  font-size: 0.813rem;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 80px;
  justify-content: center;
}

.start-btn {
  background-color: #000;
  color: white;
  border: none;
}

.start-btn:hover:not(:disabled) {
  background-color: #1a1a1a;
}

.start-btn:disabled {
  background-color: #e5e7eb;
  cursor: not-allowed;
}

.cancel-btn {
  background-color: #fff;
  color: #dc2626;
  border: 1px solid #fee2e2;
}

.cancel-btn:hover {
  background-color: #fee2e2;
}

.loading-state {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  height: 200px;
  gap: 1rem;
  color: #6b7280;
}

.loading-text {
  font-size: 0.875rem;
  color: #6b7280;
}

.spin {
  animation: spin 1s linear infinite;
  margin-bottom: 0.5rem;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Time column styles */
.time-info {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.75rem;
}

.date-display {
  margin-bottom: 0;
}

.time-wrapper {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.time {
  font-size: 0.875rem;
  font-weight: 500;
  color: #111827;
}

.timezone {
  font-size: 0.75rem;
  color: #6b7280;
}

/* Update calendar date styles */
.calendar-date {
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;
}

.calendar-month {
  color: #111827;
  font-size: 0.6rem;
  font-weight: 500;
  text-align: left;
  letter-spacing: 0.025em;
}

.calendar-day {
  font-size: 1.8rem;
  font-weight: 300;
  color: #111827;
  line-height: 1;
  text-align: left;
}

/* Make the table responsive */
@media (max-width: 1280px) {
  .time-column {
    width: 20%;
    flex: 0 0 20%;
  }

  .listing-column {
    width: 35%;
    flex: 0 0 35%;
  }

  .guest-column {
    width: 15%;
    flex: 0 0 15%;
  }

  .status-column {
    width: 10%;
    flex: 0 0 10%;
  }

  .actions-column {
    width: 15%;
    flex: 0 0 15%;
  }
}

@media (max-width: 1024px) {
  .guest-column {
    display: none;
  }

  .time-column {
    width: 22%;
    flex: 0 0 22%;
  }

  .listing-column {
    width: 38%;
    flex: 0 0 38%;
  }

  .status-column {
    width: 12%;
    flex: 0 0 12%;
  }

  .actions-column {
    width: 13%;
    flex: 0 0 13%;
  }
}

@media (max-width: 768px) {
  .actions-column {
    display: none;
  }

  .time-column {
    width: 25%;
    flex: 0 0 25%;
  }

  .listing-column {
    width: 50%;
    flex: 0 0 50%;
  }

  .status-column {
    width: 25%;
    flex: 0 0 25%;
  }
}

.section-header {
  margin: 2rem 0 1rem;
}

.section-header h3 {
  font-size: 1.25rem;
  font-weight: 600;
  color: #111827;
}

.past-section {
  margin-top: 3rem;
}

.empty-section {
  padding: 2rem;
  text-align: center;
  background-color: #f9fafb;
  border-radius: 12px;
  color: #6b7280;
}

.reservation-table {
  margin-bottom: 1rem;
  background-color: white;
  border-radius: 16px;
  overflow: hidden;
}

.dashboard-stats {
  margin-bottom: 3rem;
}

.section-header {
  margin-bottom: 32px;
}

.section-description {
  color: #6b7280;
  font-size: 16px;
  line-height: 1.5;
  margin-top: 8px;
}

.header-title {
  font-size: 1.5rem;
  color: #000;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.stats-cards {
  display: flex;
  gap: 24px;
  flex-wrap: wrap;
  width: 100%;
}

.stat-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  max-width: 200px;
  flex: 1;
  position: relative;
  border: 2px solid #f2f5f9;
  min-width: 160px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  /* transform: translateY(-5px); */
  /* box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); */
}

.stat-number {
  font-size: 45px;
  margin: 0;
  color: #000;
  font-weight: 300;
}

.stat-label {
  color: #6b7280;
  margin: 8px 0;
  font-size: 14px;
}

.stat-icon {
  position: absolute;
  top: 24px;
  right: 24px;
  color: #6b7280;
}

/* Make sure the styling is responsive */
@media (max-width: 768px) {
  .main-title {
    font-size: 2rem;
  }

  .stats-cards {
    gap: 16px;
  }

  .stat-card {
    padding: 16px;
    min-width: 120px;
  }

  .stat-number {
    font-size: 32px;
  }

  .stat-label {
    font-size: 13px;
  }

  .stat-icon {
    top: 16px;
    right: 16px;
  }
}

@media (max-width: 480px) {
  .main-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .main-title {
    font-size: 1.75rem;
  }

  .stats-cards {
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  .stat-card {
    max-width: 100%;
    padding: 14px;
    min-width: auto;
  }

  .stat-number {
    font-size: 28px;
  }

  .stat-label {
    font-size: 12px;
    line-height: 1.3;
  }
}

/* Mobile Card Layout Styles */
@media (max-width: 768px) {
  /* Hide desktop table on mobile */
  .desktop-table {
    display: none;
  }

  /* Show mobile cards */
  .mobile-cards {
    display: block;
  }

  .reservation-cards {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .reservation-card-mobile {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 12px;
    padding: 16px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .card-header-mobile {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    margin-bottom: 16px;
  }

  .time-info-mobile {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .calendar-date-mobile {
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .calendar-month-mobile {
    color: #6b7280;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    line-height: 1;
  }

  .calendar-day-mobile {
    font-size: 24px;
    font-weight: 300;
    color: #111827;
    line-height: 1;
  }

  .time-details-mobile {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .time-mobile {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
  }

  .timezone-mobile {
    font-size: 12px;
    color: #6b7280;
  }

  .status-badge-mobile {
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 11px;
    font-weight: 500;
    white-space: nowrap;
  }

  .status-badge-mobile.upcoming {
    background-color: #ecfdf5;
    color: #059669;
  }

  .status-badge-mobile.active {
    background-color: #dbeafe;
    color: #2563eb;
  }

  .status-badge-mobile.completed {
    background-color: #eff6ff;
    color: #3b82f6;
  }

  .status-badge-mobile.cancelled {
    background-color: #fee2e2;
    color: #dc2626;
  }

  .status-badge-mobile.past {
    background-color: #f3f4f6;
    color: #6b7280;
  }

  .status-badge-mobile.pending {
    background-color: #fef3c7;
    color: #d97706;
  }

  .card-content-mobile {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .listing-info-mobile {
    display: flex;
    align-items: center;
    gap: 12px;
  }

  .listing-image-mobile {
    width: 64px;
    height: 48px;
    border-radius: 8px;
    object-fit: cover;
    border: 1px solid #e5e7eb;
    flex-shrink: 0;
  }

  .listing-details-mobile {
    display: flex;
    flex-direction: column;
    gap: 4px;
    flex: 1;
    min-width: 0;
  }

  .listing-name-mobile {
    font-size: 15px;
    font-weight: 500;
    color: #111827;
    line-height: 1.3;
  }

  .listing-location-mobile {
    font-size: 13px;
    color: #6b7280;
    line-height: 1.3;
  }

  .guest-info-mobile {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px;
    background-color: #f9fafb;
    border-radius: 8px;
  }

  .guest-avatar-mobile {
    width: 32px;
    height: 32px;
    background-color: #e5e7eb;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #6b7280;
    flex-shrink: 0;
  }

  .guest-details-mobile {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .guest-name-mobile {
    font-size: 14px;
    font-weight: 500;
    color: #111827;
  }

  .guest-count-mobile {
    font-size: 12px;
    color: #6b7280;
  }

  .actions-mobile {
    display: flex;
    gap: 8px;
    justify-content: flex-end;
  }

  .action-btn-mobile {
    padding: 8px 16px;
    font-size: 13px;
    font-weight: 500;
    border-radius: 6px;
    cursor: pointer;
    transition: all 0.2s;
    min-width: 70px;
    text-align: center;
  }

  .start-btn-mobile {
    background-color: #000;
    color: white;
    border: none;
  }

  .start-btn-mobile:hover:not(:disabled) {
    background-color: #1a1a1a;
  }

  .start-btn-mobile:disabled {
    background-color: #e5e7eb;
    color: #9ca3af;
    cursor: not-allowed;
  }

  .cancel-btn-mobile {
    background-color: #fff;
    color: #dc2626;
    border: 1px solid #fecaca;
  }

  .cancel-btn-mobile:hover {
    background-color: #fef2f2;
  }
}

/* Hide mobile cards on desktop */
@media (min-width: 769px) {
  .mobile-cards {
    display: none;
  }
}
</style>
